import React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import * as LucideIcons from "lucide-react";

interface ImageSelectOption {
  value: string;
  label: string;
  icon?: string;
  image?: string;
}

interface ImageSelectFieldProps {
  options: ImageSelectOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function ImageSelectField({
  options,
  value,
  onChange,
  multiple = false,
  columns = 3,
  className,
}: ImageSelectFieldProps) {
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
    }
  };

  const isSelected = (optionValue: string) => selectedValues.includes(optionValue);

  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="h-8 w-8" /> : null;
  };

  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  };

  return (
    <div className={cn("grid gap-3", gridCols[columns], className)}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleSelect(option.value)}
          className={cn(
            "relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200",
            "hover:border-primary/50 hover:bg-accent/50",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
            isSelected(option.value)
              ? "border-primary bg-primary/10 shadow-md"
              : "border-border bg-card"
          )}
        >
          {isSelected(option.value) && (
            <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
          )}
          
          {option.image ? (
            <div className="w-full aspect-square rounded-lg overflow-hidden mb-2">
              <img
                src={option.image}
                alt={option.label}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center mb-2",
              isSelected(option.value) 
                ? "bg-primary text-primary-foreground" 
                : "bg-muted text-muted-foreground"
            )}>
              {getIcon(option.icon)}
            </div>
          )}
          
          <span className={cn(
            "text-sm font-medium text-center",
            isSelected(option.value) ? "text-primary" : "text-foreground"
          )}>
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
}
